// File: src/components/QrViewerSheet.jsx
import React, { useEffect, useRef } from 'react';
import { IoArrowBackSharp } from 'react-icons/io5';
import { LuX } from 'react-icons/lu';

function isPWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

export default function QrViewerSheet({ qrSrc, onBack, onClose }) {
    const scrollYRef = useRef(0);
    useEffect(() => {
      const themeMetaTag = document.querySelector('meta[name="theme-color"]');
      const body = document.body;
  
      // Save current scroll position and lock the body in place
      scrollYRef.current = window.scrollY || window.pageYOffset || 0;
      body.style.position = 'fixed';
      body.style.top = `-${scrollYRef.current}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
  
      // Optional: dim system UI color while sheet is open
      document.documentElement.classList.add('overlay-active');
      document.getElementById('root')?.classList.add('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#999998';
  
      return () => {
        // Restore body scroll position and styles
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        body.style.overflow = '';
        window.scrollTo(0, scrollYRef.current);
  
        document.documentElement.classList.remove('overlay-active');
        document.getElementById('root')?.classList.remove('overlay-active');
        if (themeMetaTag) themeMetaTag.content = '#ffffff';
      };
    }, []);

  return (
    <>
      <div style={S.backdrop} onClick={onClose} />
      <div
        style={S.sheet}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (Back • Title • X) */}
        <div style={S.header}>
          <button aria-label="Back" style={S.headerBackBtn} onClick={onBack}>
            <IoArrowBackSharp size={18} />
          </button>

          <div style={S.headerTitle}> QR Code</div>

          <button aria-label="Close" style={S.headerCloseBtn} onClick={onClose}>
            <LuX size={16} style={{ display: 'block' }} />
          </button>
        </div>

        {/* Hairline */}
        <hr style={S.hairline} />

        {/* Content */}
        <div style={S.content}>
          {qrSrc ? (
            <div style={S.card}>
              <div style={S.qrPanel}>
                <div style={S.qrCanvas}>
                  {/* corner brackets */}
                  <div style={{ ...S.corner, ...S.cornerTL }} />
                  <div style={{ ...S.corner, ...S.cornerTR }} />
                  <div style={{ ...S.corner, ...S.cornerBL }} />
                  <div style={{ ...S.corner, ...S.cornerBR }} />

                  <img src={qrSrc} alt="Your QR Code" style={S.qrImg} />
                </div>
              </div>

              <div style={S.captionTitle}>Show this at the door if asked</div>
              <div style={S.captionSub}>This code is unique to your ticket</div>
            </div>
          ) : (
            <div style={{ opacity: 0.6, fontSize: 13 }}>No QR available</div>
          )}
        </div>
      </div>
    </>
  );
}

  const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 998,
  },
  sheet: {
    position: 'fixed',
    left: 'calc(env(safe-area-inset-left, 0px) + 8px)',
    right: 'calc(env(safe-area-inset-right, 0px) + 8px)',
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
    background: '#FFFFFF',
    border: '1px solid #E7E9EE',
    borderRadius: 12,
    animation: 'slideUp 0.25s ease-out',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 999,
    maxHeight: 'min(80dvh, 640px)',
    boxSizing: 'border-box',
    paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
  },
  header: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    padding: '0 16px',
    boxSizing: 'border-box',
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
    textAlign: 'center',
  },
  headerBackBtn: {
    position: 'absolute',
    left: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: 'none',
    width: 32,
    height: 32,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 8,
    color: '#0b0b0b',
    cursor: 'pointer',
  },
  headerCloseBtn: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: '1px solid #e5e7eb',
    width: 32,
    height: 32,
    boxSizing: 'border-box',
    padding: 0,
    lineHeight: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    color: '#0b0b0b',
    cursor: 'pointer',
  },
  hairline: {
    border: 'none',
    borderBottom: '1px solid #eef0f2',
    margin: 0,
  },
  content: {
    padding: 24,
    paddingInline: 16,
    display: 'grid',
    placeItems: 'center',
    minHeight: 'clamp(360px, 58vh, 640px)',
    boxSizing: 'border-box',
    flex: 1,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
  },

  // Elevated card framing the QR
  card: {
    width: '100%',
    maxWidth: 440,             // hard cap
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 10px 30px rgba(213, 213, 213, 0.12)',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '0 auto',          // center horizontally
    boxSizing: 'border-box',   // include padding in width
    marginBottom: 4,
  },

  // White panel behind QR
  qrPanel: {
    width: '100%',
    borderRadius: 14,
    background: '#fff',
    padding: 1,
    marginBottom: 12,
    boxSizing: 'border-box',
  },

  // Square canvas for the QR with corner brackets
  qrCanvas: {
    position: 'relative',
    width: '100%',
    aspectRatio: '1 / 1',
    background: '#fff',
    border: '1px solid #E7E9EE',
    borderRadius: 12,
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  qrImg: {
    width: '86%',
    height: '86%',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    imageRendering: 'pixelated',
    boxSizing: 'border-box',
  },

  // Corner brackets
  corner: {
    position: 'absolute',
    width: 42,
    height: 42,
    borderColor: '#93C5FD',
    opacity: 0.9,
  },
  cornerTL: { top: 8, left: 8, borderLeft: '3px solid', borderTop: '3px solid', borderRadius: 2 },
  cornerTR: { top: 8, right: 8, borderRight: '3px solid', borderTop: '3px solid', borderRadius: 2 },
  cornerBL: { bottom: 8, left: 8, borderLeft: '3px solid', borderBottom: '3px solid', borderRadius: 2 },
  cornerBR: { bottom: 8, right: 8, borderRight: '3px solid', borderBottom: '3px solid', borderRadius: 2 },

  captionTitle: {
    marginTop: 4,
    fontSize: 14,
    fontWeight: 900,
    color: '#0b0b0b',
    textAlign: 'center',
  },
  captionSub: {
    marginTop: 2,
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
};
